import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { adsApi, campaignsApi, flagsApi } from '@/api/client';
import { automationApi } from '@/api/automationApi';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import type {
  HourlyBudgetGroupPayload,
  HourlyBudgetListItem,
  HourlyBudgetSchedulePayload,
  MissingBaseBudgetCampaign,
} from '@/types/hourlyBudget';
import type { FlagPreviewRequest } from '@/types/automationFlags';
import type { Portfolio } from '@/types/portfolios';
import {
  mapEntityApiItems,
  type AutomationCampaignOption,
} from '@/components/automation/automationCampaignScope';
import { FlagCallout } from '@/components/flags/FlagCallout';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { DeleteHourlyBudgetModal } from '@/components/DeleteHourlyBudgetModal';
import { HourlyBudgetEditor } from '@/components/hourlyBudget/HourlyBudgetEditor';
import {
  DaypartingScopePicker,
  filterCampaignsByPortfolio,
  type DaypartingScopeType,
} from '@/components/hourlyBudget/DaypartingScopePicker';
import { DaypartingEmptyState, DaypartingTable } from '@/components/hourlyBudget/DaypartingList';
import {
  MissingBaseBudgetPanel,
  SetBaseBudgetDialog,
} from '@/components/hourlyBudget/MissingBaseBudgetPanel';
import { WorkspaceTablePagination } from '@/components/workspace/WorkspaceSection';
import { useServerPagination, totalPages } from '@/hooks/useServerPagination';
import { hasErrorFlags, parseAutomationFlags } from '@/utils/automationFlags';
import {
  configuredHourlyBudgetCampaignIds,
  hourlyBudgetCampaignRef,
  hourlyBudgetCampaigns,
  hourlyBudgetGroupId,
  hourlyBudgetProduct,
  profileHourlyBudgetsQueryKey,
} from '@/utils/hourlyBudgetList';
import {
  parseDaypartingCreatePrefill,
  type DaypartingCreatePrefill,
} from '@/utils/daypartingAutomationPrefill';
import { getActiveMoneyContext } from '@/utils/formatCurrency';

function parsePortfolios(data: unknown): Portfolio[] {
  if (!data || typeof data !== 'object') return [];
  const root = data as Record<string, unknown>;
  const list = Array.isArray(root.portfolios) ? root.portfolios : [];
  return list.filter((item): item is Portfolio => !!item && typeof item === 'object' && 'portfolioId' in item);
}

function optionsFromListItem(item: HourlyBudgetListItem): AutomationCampaignOption[] {
  return hourlyBudgetCampaigns(item).map((campaign) => ({
    campaignId: campaign.campaign_id,
    entityRef: campaign.campaign_id,
    name: campaign.name,
    adProduct: campaign.campaign_type,
    state: campaign.state,
  }));
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { detail?: unknown } } };
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

type DaypartingSectionProps = {
  profileId: string;
};

export function DaypartingSection({ profileId }: DaypartingSectionProps) {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('all');
  const [productFilter, setProductFilter] = useState<'all' | CampaignAdProduct>('all');
  const [createPrefill, setCreatePrefill] = useState<DaypartingCreatePrefill | null>(() =>
    parseDaypartingCreatePrefill(location.state)
  );
  const parsedPrefill = createPrefill;
  const [editorOpen, setEditorOpen] = useState(() => Boolean(parsedPrefill));
  const [editorItem, setEditorItem] = useState<HourlyBudgetListItem | null>(null);
  const [scopeType, setScopeType] = useState<DaypartingScopeType>(() =>
    parsedPrefill?.portfolioId ? 'portfolio' : 'campaigns'
  );
  const [selectedCampaigns, setSelectedCampaigns] = useState<AutomationCampaignOption[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(
    () => parsedPrefill?.portfolioId ?? null
  );
  const [removeTarget, setRemoveTarget] = useState<HourlyBudgetListItem | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [baseTarget, setBaseTarget] = useState<MissingBaseBudgetCampaign | null>(null);
  const [baseError, setBaseError] = useState<string | null>(null);
  const prefillApplied = useRef(false);

  useEffect(() => {
    if (!parseDaypartingCreatePrefill(location.state)) return;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  const { page, pageSize, setPage, setPageSize, offset } = useServerPagination(
    `${search.trim()}|${productFilter}|${activeFilter}`
  );

  const listFilters = {
    q: search.trim() || undefined,
    active: activeFilter === 'all' ? undefined : activeFilter === 'true',
    product: productFilter === 'all' ? undefined : productFilter,
    limit: pageSize,
    offset,
  };

  const listQ = useQuery({
    queryKey: profileHourlyBudgetsQueryKey(profileId, listFilters),
    queryFn: async () => {
      const res = await campaignsApi.listHourlyBudgets(profileId, listFilters);
      if (!res.success) throw new Error('Failed to list hourly budgets');
      return res.data ?? { items: [], total: 0 };
    },
    enabled: Boolean(profileId),
    placeholderData: keepPreviousData,
  });

  const missingBaseQ = useQuery({
    queryKey: ['hourly-budgets-missing-base', profileId],
    queryFn: async () => {
      const res = await campaignsApi.listMissingBaseBudgets(profileId);
      if (!res.success) throw new Error('Failed to list campaigns missing a base budget');
      return res.data ?? { items: [], total: 0 };
    },
    enabled: Boolean(profileId),
  });

  const setBaseMut = useMutation({
    mutationFn: async (amount: number) => {
      if (!baseTarget) throw new Error('Choose a campaign');
      const res = await campaignsApi.setHourlyBudgetBase(profileId, {
        campaign_id: baseTarget.campaign_id,
        product: baseTarget.product,
        base_budget: amount,
      });
      if (!res.success) throw new Error('Failed to set base budget');
    },
    onSuccess: () => {
      showToast('Base budget saved', 'success');
      setBaseTarget(null);
      setBaseError(null);
      void qc.invalidateQueries({ queryKey: ['hourly-budgets-missing-base', profileId] });
      void qc.invalidateQueries({ queryKey: ['profile-hourly-budgets', profileId] });
    },
    onError: (error: unknown) => {
      setBaseError(apiErrorMessage(error, 'Failed to set base budget'));
    },
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const filtersActive = Boolean(listFilters.q || listFilters.active != null || listFilters.product);
  const showEmptyState = !listQ.isLoading && !listQ.error && total === 0 && !filtersActive;

  useEffect(() => {
    if (!listQ.data || listQ.isFetching) return;
    const pages = totalPages(total, pageSize);
    if (page > pages) setPage(pages);
  }, [listQ.data, listQ.isFetching, total, pageSize, page, setPage]);

  const allConfiguredQ = useQuery({
    queryKey: profileHourlyBudgetsQueryKey(profileId),
    queryFn: async () => {
      const res = await campaignsApi.listHourlyBudgets(profileId);
      if (!res.success) throw new Error('Failed to list hourly budgets');
      return res.data ?? { items: [], total: 0 };
    },
    enabled: Boolean(profileId) && editorOpen,
  });

  const configuredIds = useMemo(() => {
    const allItems = allConfiguredQ.data?.items ?? items;
    const skipCurrent = new Set(
      editorItem ? hourlyBudgetCampaigns(editorItem).map((c) => c.campaign_id) : []
    );
    return configuredHourlyBudgetCampaignIds(allItems).filter((id) => !skipCurrent.has(id));
  }, [allConfiguredQ.data?.items, items, editorItem]);

  const entitiesQ = useQuery({
    queryKey: ['automation-entities', profileId, 'dayparting'],
    queryFn: () =>
      automationApi
        .listEntities(profileId, {
          entity_type: 'campaign',
          ad_types: 'SP,SB,SD',
        })
        .then((r) => mapEntityApiItems(r.data?.items ?? [])),
    enabled: Boolean(profileId) && editorOpen,
  });

  const portfoliosQ = useQuery({
    queryKey: ['ads-portfolios', profileId, 'dayparting'],
    queryFn: async () => {
      const res = await adsApi.listPortfolios(profileId);
      if (!res.success) throw new Error('Failed to list portfolios');
      return parsePortfolios(res.data);
    },
    enabled: Boolean(profileId) && editorOpen,
  });

  const pickerOptions = useMemo(() => entitiesQ.data ?? [], [entitiesQ.data]);

  const previewCampaigns = useMemo(
    () => filterCampaignsByPortfolio(pickerOptions, selectedPortfolioId),
    [pickerOptions, selectedPortfolioId]
  );

  useEffect(() => {
    if (!editorOpen || editorItem || prefillApplied.current) return;
    const ids = createPrefill?.campaignIds ?? [];
    if (ids.length === 0 || !entitiesQ.data) return;
    const byId = new Map(entitiesQ.data.map((option) => [option.campaignId, option]));
    setSelectedCampaigns(
      ids.map(
        (id) =>
          byId.get(id) ?? {
            campaignId: id,
            entityRef: id,
            name: null,
            adProduct: 'SP',
          }
      )
    );
    setScopeType('campaigns');
    prefillApplied.current = true;
  }, [editorOpen, editorItem, createPrefill, entitiesQ.data]);

  const deleteMut = useMutation({
    mutationFn: async (item: HourlyBudgetListItem) => {
      const groupId = hourlyBudgetGroupId(item);
      if (groupId) {
        const res = await campaignsApi.deleteHourlyBudgetGroup(profileId, groupId);
        if (!res.success) throw new Error('Failed to remove hourly budget');
        return;
      }
      const campaignRef = hourlyBudgetCampaignRef(item);
      const product = hourlyBudgetProduct(item);
      const res = await campaignsApi.deleteCampaignHourlyBudget(profileId, campaignRef, {
        product,
      });
      if (!res.success) throw new Error('Failed to remove hourly budget');
    },
    onSuccess: () => {
      showToast('Hourly budget removed', 'success');
      setRemoveTarget(null);
      setRemoveError(null);
      void qc.invalidateQueries({ queryKey: ['profile-hourly-budgets', profileId] });
      void qc.invalidateQueries({ queryKey: ['hourly-budgets-missing-base', profileId] });
    },
    onError: (e: unknown) => {
      setRemoveError(apiErrorMessage(e, 'Failed to remove hourly budget'));
    },
  });

  const resetEditorScope = (item: HourlyBudgetListItem | null, prefill: DaypartingCreatePrefill | null) => {
    prefillApplied.current = Boolean(prefill?.campaignIds?.length);
    if (item) {
      setScopeType(item.source_type === 'portfolio' ? 'portfolio' : 'campaigns');
      setSelectedCampaigns(optionsFromListItem(item));
      setSelectedPortfolioId(item.portfolio?.portfolioId ?? null);
      return;
    }
    setScopeType(prefill?.portfolioId ? 'portfolio' : 'campaigns');
    setSelectedCampaigns([]);
    setSelectedPortfolioId(prefill?.portfolioId ?? null);
  };

  const openCreate = () => {
    setCreatePrefill(null);
    setEditorItem(null);
    resetEditorScope(null, null);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorItem(null);
    setCreatePrefill(null);
    setSelectedCampaigns([]);
    setSelectedPortfolioId(null);
    setScopeType('campaigns');
    prefillApplied.current = false;
  };

  const openEditorForItem = (item: HourlyBudgetListItem) => {
    setCreatePrefill(null);
    setEditorItem(item);
    resetEditorScope(item, null);
    setEditorOpen(true);
  };

  const handleScopeTypeChange = (next: DaypartingScopeType) => {
    setScopeType(next);
    if (next === 'campaigns') setSelectedPortfolioId(null);
    else setSelectedCampaigns([]);
  };

  const hasScope =
    scopeType === 'campaigns' ? selectedCampaigns.length > 0 : Boolean(selectedPortfolioId);

  const excludeGroupId = editorItem ? hourlyBudgetGroupId(editorItem) : null;
  const previewRequest = useMemo((): FlagPreviewRequest | null => {
    if (!editorOpen || !hasScope) return null;
    return {
      domain: 'dayparting',
      source_type: scopeType,
      ...(scopeType === 'campaigns'
        ? {
            campaign_refs: selectedCampaigns.map((campaign) => ({
              campaign_id: campaign.campaignId,
              product: campaign.adProduct,
            })),
          }
        : { portfolio_id: selectedPortfolioId }),
      ...(excludeGroupId ? { exclude_group_id: excludeGroupId } : {}),
    };
  }, [
    editorOpen,
    excludeGroupId,
    hasScope,
    scopeType,
    selectedCampaigns,
    selectedPortfolioId,
  ]);

  const [debouncedPreviewRequest, setDebouncedPreviewRequest] = useState(previewRequest);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedPreviewRequest(previewRequest), 250);
    return () => window.clearTimeout(timer);
  }, [previewRequest]);

  const flagsPreviewQ = useQuery({
    queryKey: ['automation-flags-preview', profileId, debouncedPreviewRequest],
    queryFn: async () => {
      if (!debouncedPreviewRequest) return [];
      const res = await flagsApi.preview(profileId, debouncedPreviewRequest);
      if (!res.success) throw new Error('Failed to preview flags');
      return parseAutomationFlags(res.data?.flags);
    },
    enabled: Boolean(profileId && debouncedPreviewRequest),
  });
  const previewFlags =
    debouncedPreviewRequest === previewRequest ? (flagsPreviewQ.data ?? []) : [];
  const saveBlockedByFlags = hasErrorFlags(previewFlags);

  const persistGroup = async (payload: HourlyBudgetSchedulePayload) => {
    const body: HourlyBudgetGroupPayload = {
      ...payload,
      source_type: scopeType,
      ...(scopeType === 'campaigns'
        ? {
            campaign_refs: selectedCampaigns.map((campaign) => ({
              campaign_id: campaign.campaignId,
              product: (['SP', 'SB', 'SD'].includes(campaign.adProduct.toUpperCase())
                ? campaign.adProduct.toUpperCase()
                : 'SP') as CampaignAdProduct,
            })),
          }
        : { portfolio_id: selectedPortfolioId ?? undefined }),
    };
    try {
      const groupId = editorItem ? hourlyBudgetGroupId(editorItem) : null;
      const res = groupId
        ? await campaignsApi.updateHourlyBudgetGroup(profileId, groupId, body)
        : await campaignsApi.createHourlyBudgetGroup(profileId, body);
      if (!res.success) throw new Error('Failed to save hourly budget');
      showToast('Hourly budget saved', 'success');
    } catch (err) {
      throw new Error(apiErrorMessage(err, 'Failed to save hourly budget'));
    }
  };

  const deleteGroup = async () => {
    if (!editorItem) return;
    const groupId = hourlyBudgetGroupId(editorItem);
    if (groupId) {
      const res = await campaignsApi.deleteHourlyBudgetGroup(profileId, groupId);
      if (!res.success) throw new Error('Failed to remove hourly budget');
      return;
    }
    const campaignRef = hourlyBudgetCampaignRef(editorItem);
    const res = await campaignsApi.deleteCampaignHourlyBudget(profileId, campaignRef, {
      product: hourlyBudgetProduct(editorItem),
    });
    if (!res.success) throw new Error('Failed to remove hourly budget');
  };

  return (
    <>
      <div className="space-y-3">
        {missingBaseQ.isError ? (
          <p className="text-sm text-error">
            {(missingBaseQ.error as Error).message || 'Failed to load campaigns missing a base budget'}
          </p>
        ) : (
          <MissingBaseBudgetPanel
            items={missingBaseQ.data?.items ?? []}
            onSetBase={(item) => {
              setBaseError(null);
              setBaseTarget(item);
            }}
          />
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="rounded border border-border px-3 py-1.5 text-sm"
            placeholder="Search campaigns"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search campaigns"
          />
          <select
            className="rounded border border-border bg-surface px-3 py-1.5 text-sm"
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value as 'all' | CampaignAdProduct)}
            aria-label="Filter by product"
          >
            <option value="all">All products</option>
            <option value="SP">SP</option>
            <option value="SB">SB</option>
            <option value="SD">SD</option>
          </select>
          <select
            className="rounded border border-border bg-surface px-3 py-1.5 text-sm"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as 'all' | 'true' | 'false')}
            aria-label="Filter by active"
          >
            <option value="all">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <Button className="ml-auto" onClick={openCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Add hourly budget
          </Button>
        </div>

        {listQ.isLoading ? (
          <Spinner />
        ) : listQ.error ? (
          <p className="text-sm text-error">
            {(listQ.error as Error).message || 'Failed to load hourly budgets'}
          </p>
        ) : showEmptyState ? (
          <DaypartingEmptyState onAdd={openCreate} />
        ) : total === 0 ? (
          <p className="py-8 text-center text-sm text-foreground-muted">
            No campaigns match these filters.
          </p>
        ) : (
          <>
            <DaypartingTable
              profileId={profileId}
              items={items}
              onEdit={openEditorForItem}
              onRemove={(item) => {
                setRemoveError(null);
                setRemoveTarget(item);
              }}
            />
            <WorkspaceTablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={listQ.isFetching}
            />
          </>
        )}
      </div>

      {editorOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 p-4"
          onClick={closeEditor}
        >
          <div
            className="relative w-full max-w-7xl overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
              <h2 className="text-base font-semibold text-foreground">Configure hourly budget</h2>
              <button
                type="button"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-foreground-muted hover:bg-page hover:text-foreground"
                onClick={closeEditor}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4 px-5 pt-4">
              <DaypartingScopePicker
                scopeType={scopeType}
                onScopeTypeChange={handleScopeTypeChange}
                campaignOptions={pickerOptions}
                selectedCampaigns={selectedCampaigns}
                onCampaignsChange={setSelectedCampaigns}
                campaignsLoading={entitiesQ.isLoading}
                portfolios={portfoliosQ.data ?? []}
                selectedPortfolioId={selectedPortfolioId}
                onPortfolioChange={setSelectedPortfolioId}
                portfoliosLoading={portfoliosQ.isLoading}
                previewCampaigns={previewCampaigns}
                conflictCampaignIds={configuredIds}
              />
              {previewFlags.length > 0 ? <FlagCallout flags={previewFlags} /> : null}
              {createPrefill ? (
                <p className="text-xs text-foreground-subtle">
                  Matching heatmap hours are selected in the schedule below.
                </p>
              ) : null}
            </div>
            <HourlyBudgetEditor
              key={editorItem?.id ?? 'create'}
              profileId={profileId}
              campaignRef={
                editorItem && !hourlyBudgetGroupId(editorItem)
                  ? hourlyBudgetCampaignRef(editorItem)
                  : undefined
              }
              product={editorItem ? hourlyBudgetProduct(editorItem) : undefined}
              hideTitleBar
              skipFetch
              hydrateFrom={editorItem as unknown as Record<string, unknown> | null}
              hideDelete={!editorItem}
              saveDisabled={!hasScope || saveBlockedByFlags}
              initialWeeklySlots={createPrefill?.weeklySlots}
              persistOverride={persistGroup}
              deleteOverride={editorItem ? deleteGroup : undefined}
              onSaved={() => {
                void qc.invalidateQueries({ queryKey: ['profile-hourly-budgets', profileId] });
                void qc.invalidateQueries({ queryKey: ['hourly-budgets-missing-base', profileId] });
              }}
              onDeleted={() => {
                closeEditor();
                void qc.invalidateQueries({ queryKey: ['profile-hourly-budgets', profileId] });
                void qc.invalidateQueries({ queryKey: ['hourly-budgets-missing-base', profileId] });
              }}
            />
          </div>
        </div>
      )}

      {baseTarget ? (
        <SetBaseBudgetDialog
          item={baseTarget}
          countryCode={getActiveMoneyContext().countryCode}
          accountType={getActiveMoneyContext().accountType}
          saving={setBaseMut.isPending}
          error={baseError}
          onSave={(amount) => setBaseMut.mutate(amount)}
          onClose={() => {
            if (!setBaseMut.isPending) {
              setBaseTarget(null);
              setBaseError(null);
            }
          }}
        />
      ) : null}

      <DeleteHourlyBudgetModal
        isOpen={Boolean(removeTarget)}
        hourlyBudget={
          removeTarget?.hourly_budget != null
            ? String(removeTarget.hourly_budget)
            : removeTarget?.base_budget != null
              ? String(removeTarget.base_budget)
              : ''
        }
        deleting={deleteMut.isPending}
        error={removeError}
        onConfirm={() => {
          if (removeTarget) deleteMut.mutate(removeTarget);
        }}
        onCancel={() => {
          if (!deleteMut.isPending) {
            setRemoveTarget(null);
            setRemoveError(null);
          }
        }}
      />
    </>
  );
}
